// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import JettingAppTextField, {
    JettingAppTextFieldStyles,
} from "@stoneage/App/Components/JettingAppComponents/JettingAppTextField";
import Nouislider from "nouislider-react";
import React, { useEffect, useLayoutEffect, useState } from "react";

interface Props {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    tooltipText?: string;
    currentUnit?: string;
    units: { [key: string]: string };
    message?: { message: string; type: string };
    onChange: (number: number) => void;
    extendedStyles?: JettingAppSliderStyles;
    readOnly?: boolean;
}

export interface JettingAppSliderStyles {
    wrapper?: InjectableCss;
    textWrapper?: InjectableCss;
    textFieldStyles?: JettingAppTextFieldStyles;
}

export const jettingAppSliderStyles: JettingAppSliderStyles = {};

const JettingAppSlider = ({
    extendedStyles,
    label,
    value,
    onChange,
    min,
    max,
    step,
    tooltipText,
    units,
    currentUnit,
    message,
    readOnly,
}: Props) => {
    const [styles] = useState(() => mergeToNew(jettingAppSliderStyles, extendedStyles));
    const [inputValue, setInputValue] = useState(((value < min ? min : value) || min)?.toString() || "0");
    const [updatedValue, setUpdatedValue] = useState(value);

    // Prevent slider jumping
    useLayoutEffect(() => {
        setUpdatedValue(Number(inputValue));
    }, [inputValue]);

    useEffect(() => {
        if (value === Number(inputValue)) {
            return;
        }
        setInputValue(`${value}`);
    }, [value]);

    const onInputChange = (values: string[]) => {
        setInputValue(values?.[0]);
        onChange(Number(values?.[0]));
    };

    const onTextFieldChange = (value: number) => {
        setUpdatedValue(value);
        setInputValue(value?.toString());
        onChange(value);
    };

    const isNullOrUndefined = (num: number | null | undefined): boolean => num == null;

    if (isNullOrUndefined(min) || isNullOrUndefined(max)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} className={message?.message ? "has-error" : ""}>
            <StyledWrapper {...styles.textWrapper}>
                <JettingAppTextField
                    label={label}
                    value={updatedValue}
                    tooltipText={tooltipText}
                    currentUnit={currentUnit}
                    units={units}
                    message={message}
                    onChange={onTextFieldChange}
                    labelIsHorizontal={true}
                    extendedStyles={styles.textFieldStyles}
                    maxValue={max}
                    minValue={min}
                    readOnly={readOnly}
                />
            </StyledWrapper>
            <Nouislider
                range={{ min, max }}
                start={updatedValue}
                step={step}
                pips={{
                    mode: "count",
                    density: 5,
                    values: 6,
                    format: {
                        to: (value: any) => (value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)), // Ensures decimal formatting
                        from: (value: Number) => Number(value),
                    },
                }}
                onChange={onInputChange}
            />
        </StyledWrapper>
    );
};
export default JettingAppSlider;
